import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const DataUploader = ({ sessionId, projectCode, onUploadSuccess, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file || !sessionId || !projectCode) return;

    // Validate file type
    const validExtensions = ['csv', 'xlsx', 'xls'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      setUploadStatus({ type: 'error', message: 'Invalid file type. Use CSV or Excel.' });
      return;
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadStatus({ type: 'error', message: 'File too large. Maximum 100MB.' });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sessionId', sessionId);
      formData.append('projectCode', projectCode);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/insights/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setUploadStatus({ type: 'success', message: `Uploaded ${data.rowCount} rows successfully` });
      setUploadedFile({ name: file.name, rowCount: data.rowCount });
      onUploadSuccess(data);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({ type: 'error', message: error.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      <UploadArea
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        disabled={disabled || uploading}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />
        
        <CloudUploadIcon style={{ fontSize: '3rem', color: disabled ? '#ccc' : '#008080', marginBottom: '1rem' }} />
        
        <UploadText>
          {uploading ? 'Uploading...' : 'Click to upload CSV or Excel file'}
        </UploadText>
        
        <UploadHint>
          Max 100MB • Must contain 'responder_id' column
        </UploadHint>
      </UploadArea>

      {uploadStatus && (
        <StatusMessage type={uploadStatus.type}>
          {uploadStatus.type === 'success' ? (
            <CheckCircleIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
          ) : (
            <ErrorIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
          )}
          {uploadStatus.message}
        </StatusMessage>
      )}

      {uploadedFile && (
        <FileInfo>
          <FileName>{uploadedFile.name}</FileName>
          <RowCount>{uploadedFile.rowCount.toLocaleString()} rows loaded</RowCount>
        </FileInfo>
      )}
    </Container>
  );
};

export default DataUploader;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const UploadArea = styled.div`
  border: 2px dashed ${(props) => (props.disabled ? '#e0e0e0' : '#008080')};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${(props) => (props.disabled ? '#f5f5f5' : 'white')};
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.disabled ? '#f5f5f5' : '#f0f9f9')};
    border-color: ${(props) => (props.disabled ? '#e0e0e0' : '#006666')};
  }
`;

const UploadText = styled.div`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const UploadHint = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${(props) => (props.type === 'success' ? '#d4edda' : '#f8d7da')};
  color: ${(props) => (props.type === 'success' ? '#155724' : '#721c24')};
  border: 1px solid ${(props) => (props.type === 'success' ? '#c3e6cb' : '#f5c6cb')};
`;

const FileInfo = styled.div`
  background: #f0f9f9;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #008080;
`;

const FileName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
`;

const RowCount = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
`;
